import {
  Bell,
  Wrench,
  FileOutput,
  Settings,
  Lock,
  Users,
  UserCheck,
  BarChart3,
  CheckSquare,
  Building,
  DollarSign,
  CalendarDays,
  FileText,
  BookOpen,
  CreditCard,
  Shield,
  Activity,
  User,
  KeyRound,
} from "lucide-react";
import { LucideIcon } from "lucide-react";


export type NavItem = {
  icon: LucideIcon;
  label: string;
  href: string;
};

/**
 * Canonical Settings tab configuration — SINGLE SOURCE OF TRUTH.
 *
 * Adding a tab? Add an entry here. Renaming a tab? Update label here.
 * Both SettingsRouter (the route map) and SettingsTabNavigation (the tab strip)
 * import from this constant.
 *
 * `key` is the URL query-param value (?tab={key}).
 * `legacyKeys` lets old saved bookmarks redirect to the new key.
 */
export type SettingsTabKey =
  | 'profile'
  | 'general'
  | 'password'
  | 'notifications'
  | 'company'
  | 'team'
  | 'billing'
  | 'privacy-security';

export interface SettingsTabConfig {
  key: SettingsTabKey;
  label: string;
  icon: LucideIcon;
  description?: string;
  /** Old query param values that should redirect to this tab's current key. */
  legacyKeys?: string[];
}

export const SETTINGS_TABS_CONFIG: SettingsTabConfig[] = [
  {
    key: 'profile',
    label: 'Profile',
    icon: User,
    description: 'Personal information, contact details, address',
  },
  {
    key: 'general',
    label: 'General',
    icon: Settings,
    description: 'Theme, display, and user preferences',
  },
  {
    key: 'password',
    label: 'Password',
    icon: KeyRound,
    description: 'Change your password',
  },
  {
    key: 'notifications',
    label: 'Notifications',
    icon: Bell,
    description: 'Email and in-app notification preferences',
  },
  {
    key: 'company',
    label: 'Company',
    icon: Building,
    description: 'Company information and business details',
    legacyKeys: ['account'],
  },
  {
    key: 'team',
    label: 'Team',
    icon: Users,
    description: 'Team member and permission management',
    legacyKeys: ['team-access'],
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: CreditCard,
    description: 'Subscription and payment management',
  },
  {
    key: 'privacy-security',
    label: 'Privacy & Security',
    icon: Shield,
    description: 'Data export and privacy settings',
    legacyKeys: ['data-privacy'],
  },
];

export const DEFAULT_SETTINGS_TAB: SettingsTabKey = 'profile';

/** Map any incoming key (current or legacy) to a current canonical key, or null if unknown. */
export function resolveSettingsTabKey(input: string | null | undefined): SettingsTabKey | null {
  if (!input) return null;
  const direct = SETTINGS_TABS_CONFIG.find((t) => t.key === input);
  if (direct) return direct.key;
  const viaLegacy = SETTINGS_TABS_CONFIG.find((t) => t.legacyKeys?.includes(input));
  return viaLegacy ? viaLegacy.key : null;
}

export const mainNavItems: NavItem[] = [
  { icon: BarChart3, label: "Dashboard", href: "/dashboard" },
  { icon: BarChart3, label: "Consultants", href: "/consultants" },
  { icon: Users, label: "Client List", href: "/clients" },
  { icon: Building, label: "Company", href: "/company" },
  { icon: DollarSign, label: "Valuations", href: "/valuations" },
  { icon: BarChart3, label: "Benchmarks", href: "/benchmarks" },
  { icon: Users, label: "Team", href: "/team" },
  { icon: UserCheck, label: "Successors", href: "/successors" },
  { icon: CalendarDays, label: "Plan", href: "/plan" },
  { icon: CheckSquare, label: "Tasks", href: "/tasks" },
  { icon: FileText, label: "Documents", href: "/documents" },
  { icon: BookOpen, label: "Learn", href: "/learn" },
  { icon: FileText, label: "Report Center", href: "/reports" },
  { icon: Settings, label: "Settings", href: "/settings" },
];




export const platformAdminNavItems = [
  {
    href: '/admin',
    label: 'Platform Admin',
    icon: Shield,
    description: 'Platform administration dashboard'
  },
  {
    href: '/admin/audit',
    label: 'Audit Logs',
    icon: Activity,
    description: 'Platform admin audit trail'
  }
];
